import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { useCreditsDuplexer } from './use-credits-duplexer';
import { queryClient, queryKeys } from './query-client';
// Type-only, so the rpc barrel `credits-overview` pulls in never reaches this module's own tests.
import type { DailyLimit } from './credits-overview';

// API is always mounted under /orion-web on both brands (see lib/api-base).

// A window event any surface fires after an action that changes the balance (e.g. a completed
// purchase) so the always-mounted sidebar chip refreshes without a full page reload. The grant is
// webhook-driven and lands a beat after the event, so listeners poll briefly rather than refetch once.
export const CREDITS_REFRESH_EVENT = 'orion:credits-refresh';

// Ramped refetch cadence after a balance-changing action — fast early, then widening, ~30s total —
// to catch the async webhook grant without hammering /api/credits/balance. Stops early once the
// balance grows past its pre-action value.
const REFRESH_DELAYS_MS = [1500, 2000, 2500, 3000, 3000, 3000, 3000, 3000, 3000, 3000];

/** Grace after the stated reset before re-reading, so the server's own roll lands first. */
const DAILY_RESET_SKEW_MS = 5_000;
/**
 * How far past the skew a client may be pushed, picked per client.
 *
 * The reset is a single GLOBAL instant — UTC midnight — not a per-account anniversary, so without
 * this every capped session in the world re-reads its balance inside the same few seconds. That is a
 * self-inflicted thundering herd on `/api/credits/balance`, and it grows with the rollout rather than
 * showing up during it.
 *
 * Spread over two minutes, which costs the user nothing: the card is being refreshed in the
 * background at an hour they are usually not watching (UTC midnight is 2-3am in Israel, 4-5pm for
 * US-West), and a bar that updates a minute late is indistinguishable from one that updates on time.
 */
const DAILY_RESET_JITTER_MS = 120_000;
/** Never hold a timer for longer than a day's boundary could legitimately be away. A value past this
 *  is a bad clock or a bad payload, and the ordinary staleness path handles it. */
const MAX_DAILY_RESET_WAIT_MS = 25 * 60 * 60 * 1000;

/**
 * When to re-read the balance for a stated daily reset, or null for "don't".
 *
 * `0` means the boundary has already passed and the read is due NOW — which is a real case, not a
 * degenerate one: a page can be restored from bfcache hours later, and a failed attempt leaves the
 * cache holding a `resetsAt` that is already behind us. Returning null there (the first version of
 * this) is what made one transient failure permanent.
 *
 * Pure and exported so the timing is testable without a fake clock in React.
 */
export function dailyResetDelayMs(
  resetsAtIso: string | null | undefined,
  now: number,
  jitterMs = Math.random() * DAILY_RESET_JITTER_MS,
): number | null {
  if (!resetsAtIso) return null;
  const dueAt = new Date(resetsAtIso).getTime();
  if (Number.isNaN(dueAt)) return null;
  const dueIn = dueAt + DAILY_RESET_SKEW_MS - now;
  if (dueIn > MAX_DAILY_RESET_WAIT_MS) return null;
  // Jitter applies only to a boundary still AHEAD of us. A reset already behind us is a recovery
  // (a failed attempt, or a tab restored from bfcache) rather than the global midnight stampede,
  // so it is read immediately — those are spread over real time already, and delaying them would
  // just extend how long a stale card keeps claiming the day is spent.
  return dueIn > 0 ? dueIn + jitterMs : 0;
}

/**
 * Re-read the balance until the day has actually rolled — bounded, then it gives up.
 *
 * A single `invalidateQueries` was not enough, and the reason is specific to this client: `retry` is
 * FALSE, so one transient 500 leaves React Query holding yesterday's payload — including the same
 * now-past `resetsAtIso`. The effect's dependency therefore never changes, `refetchOnWindowFocus` is
 * false, and the card sits at "Daily 100 of 100" forever: the exact bug the timer was added to fix.
 *
 * Success is the SERVER's `resetsAt` moving, not the request returning 200. That covers the second
 * way this fails — a boundary read a beat too early re-reads the spent day and would otherwise be
 * counted as done — and it makes the 5s skew a hint rather than a load-bearing constant.
 *
 * Bounded on purpose. Retrying until it works would be a hot loop against a server that is genuinely
 * still reporting the old day. On giving up the card stays stale until the next mount, which
 * refetches anyway (the data is well past `staleTime` by then), so the failure mode is "one
 * navigation late" rather than "wrong until reload".
 */
const DAILY_RESET_RETRY_DELAYS_MS = [0, 5_000, 15_000, 30_000, 60_000, 120_000];

/** The boundary a timer or burst has already been claimed for — module-level, so N mounted hooks
 *  schedule ONE of each rather than one apiece. */
let dailyResetClaimed: string | null = null;
let dailyResetTimer: ReturnType<typeof setTimeout> | null = null;

async function rollDailyLimit(staleResetsAt: string) {
  for (const delay of DAILY_RESET_RETRY_DELAYS_MS) {
    if (delay > 0) await new Promise((resolve) => setTimeout(resolve, delay));
    try {
      const data = await queryClient.fetchQuery({
        queryKey: queryKeys.creditsBalance,
        queryFn: fetchCreditsBalance,
        staleTime: 0,
      });
      // The day has rolled when the SERVER says a new boundary, not when a request succeeds.
      if (data.account?.dailyLimit?.resetsAtIso !== staleResetsAt) return;
    } catch {
      // Transient — spend an attempt and try again. `retry: false` means nothing else will.
    }
  }
  // Gave up. `dailyResetClaimed` deliberately stays on this boundary so the burst cannot restart
  // itself into a loop; the next mount's own refetch is what recovers.
}

/** Arm the roll for a stated boundary, at most once per distinct boundary across the whole app. */
function scheduleDailyReset(resetsAtIso: string, delayMs: number) {
  if (dailyResetClaimed === resetsAtIso) return;
  dailyResetClaimed = resetsAtIso; // claimed BEFORE any await, so two mounts cannot both arm it
  if (delayMs === 0) {
    void rollDailyLimit(resetsAtIso);
    return;
  }
  if (dailyResetTimer) clearTimeout(dailyResetTimer);
  dailyResetTimer = setTimeout(() => {
    dailyResetTimer = null;
    void rollDailyLimit(resetsAtIso);
  }, delayMs);
}

/**
 * The credits account, as shaped by /api/credits/balance — every field from the Symphony BFF's
 * GetCreditsOverview, which is the only source. Null when that read was unavailable.
 *
 * It used to carry the legacy Wixel ledger's `balance`/`cap`/`usagePct`/`period` alongside these, and
 * the meter fell back to them whenever the BFF pair was missing — which is how one account showed two
 * balances. Those four fields are gone; two of them had no reader at all.
 */
export interface CreditAccount {
  /** When the balance below is restored — the BFF's own reset date, never the legacy pool's. */
  resetDateIso: string | null;
  /** From the BFF's `plan.kind`, so a paid Symphony plan reads as paid even with no Wixel subscription. */
  isFreePlan: boolean;
  /** The BFF plan's own display name (what native's `creditsPlanDisplayName` shows), or null/absent
   *  when it sends none. The paid-branch title falls back to this when the picker catalog cannot name
   *  the plan — a legacy Wixel "Max" has no catalog key — before resorting to the generic label. */
  planName?: string | null;
  /** Every spendable credit: period remainder + top-up. */
  combinedCredits?: number | null;
  /** The pool `combinedCredits` is measured against — period allotment + top-up, so the two halves of
   *  a meter come from one pool. Null/absent when the BFF reported no positive pool. */
  combinedAllotment?: number | null;
  /**
   * How much of `combinedCredits` is the NON-RESETTING half (the BFF's `topupRemaining`).
   *
   * Only the low-credits copy reads it, and only to answer one question the fold cannot: is what is
   * left going to survive the reset? An account whose plan allotment is spent and whose remainder is
   * all this was told "upgrade so your agents don't pause before your credits renew on {date}", which
   * is false about credits that are not tied to that date.
   *
   * **Never a provenance signal.** This bucket holds purchased top-ups AND goodwill grants (agent
   * premium's `GrantGoodwillCredits` writes the same field), so no copy built on it may say the user
   * bought anything.
   */
  topupRemaining?: number | null;
  /**
   * Today's per-day ceiling, present ONLY when one is actually being enforced on this account.
   *
   * A SECOND pool with its own rule, not a slice of the one above: the period balance can be full
   * while today's allowance is spent, and the two are exhausted for opposite reasons with opposite
   * remedies — an empty wallet is fixed by paying, a spent day only by waiting. So a surface that
   * draws this must never offer a purchase off it.
   *
   * Absence is UNKNOWN, not "uncapped" — see {@link DailyLimit}.
   */
  dailyLimit?: DailyLimit | null;
  /** End of the free-usage window (promo free hours), ISO, or null/absent when none is open. See
   *  `CombinedCredits.freeUsageUntilIso`. */
  freeUsageUntilIso?: string | null;
}

/**
 * What a credits meter renders. Every surface derives its numbers here rather than reaching into
 * `account` itself, so no two surfaces can measure against different pools.
 *
 * `balance` is what is LEFT and `used` is what is gone. They are easy to mistake for
 * each other and the mistake is invisible: a caller that shows remaining under a "used"
 * label reads as "nothing spent" at the exact moment the allowance is exhausted.
 */
export interface CreditsMeter {
  /** Credits remaining this period. */
  balance: number;
  /** The allotment `balance` and `used` are measured against; 0 when unknown. */
  cap: number;
  /** Credits already spent this period — `cap - balance`, never negative. */
  used: number;
  /** Whole-number percent of the cap spent, or null when there is no cap to measure. */
  usedPct: number | null;
}

/**
 * Null means the balance is UNKNOWN — there is deliberately no second pool to fall back to.
 *
 * This used to answer from the legacy Wixel ledger whenever the BFF pair was absent (a timeout, mode
 * UNKNOWN/LEGACY_ONLY, a source reporting unavailable), so one account had two balances and the loser
 * was silent: a user's `user.md` read `99% (494 of 500 this period)` while his own card read
 * `494 of 1500` off a Wixel premium, and he quoted the card back at the agent mid-conversation. The
 * charging gate follows the BFF, so a sub-agent stopped for no credits while the card showed ~1000 left.
 *
 * A call site that renders `0` for an unknown balance tells the same lie in the other direction — an
 * exhausted account — so each one renders nothing instead of substituting a number.
 */
export function resolveCreditsMeter(state: Pick<CreditsState, 'account'>): CreditsMeter | null {
  const balance = state.account?.combinedCredits ?? null;
  const cap = state.account?.combinedAllotment ?? null;
  if (balance === null || cap === null) return null;
  // Both halves already include top-up (see lib/credits-overview.ts), so nothing is added on top here.
  const used = Math.max(0, Math.min(cap, cap - balance));
  return { balance, cap, used, usedPct: cap > 0 ? Math.round((100 * used) / cap) : null };
}

/** What the DAILY-limit bar renders — the period meter's counterpart for today's allowance. */
export interface DailyLimitMeter {
  /** Credits allowed per UTC day. Always positive, so `usedPct` always has a cap to measure against. */
  limit: number;
  /** Credits spent today. Reported as-is and may exceed `limit` — see `usedPct` for why the two differ. */
  used: number;
  /** What today still allows, per the server. */
  remaining: number;
  /** Whole-number percent of today's allowance spent, CLAMPED to 0..100 — the bar's width. */
  usedPct: number;
  /** The next UTC midnight as ISO-8601, or null when the server sent none. */
  resetsAtIso: string | null;
}

/**
 * Today's ceiling as a meter, or null when this account has no known one.
 *
 * Null is the ONLY "no daily limit" signal, and it deliberately covers both "no ceiling applies" and
 * "we could not tell" (see {@link DailyLimit}) — a surface renders nothing for either, because the
 * bar's whole claim is that a wall exists, and the app has already told a capped user there was no
 * daily limit once by inverting exactly this.
 *
 * `usedPct` is clamped where `used` is not: the single turn that crosses the ceiling is charged in
 * full, so `used` can legitimately exceed `limit`, and a bar wider than its track is a rendering bug
 * where "105 of 100 used today" is simply the truth.
 */
export function resolveDailyLimitMeter(state: Pick<CreditsState, 'account'>): DailyLimitMeter | null {
  const daily = state.account?.dailyLimit;
  if (!daily || daily.limit <= 0) return null;
  const used = Math.max(0, daily.used);
  return {
    limit: daily.limit,
    used,
    remaining: Math.max(0, daily.remaining),
    usedPct: Math.min(100, Math.round((100 * used) / daily.limit)),
    resetsAtIso: daily.resetsAtIso,
  };
}

export interface CreditsState {
  loading: boolean;
  failed: boolean;
  hasMetaSite: boolean;
  /** Best number to show: the combined (agent-premiums) balance when present, else the real
   *  account balance, else the plain balance. */
  balance: number | null;
  /** Full breakdown for the detailed card; null when unavailable. */
  account: CreditAccount | null;
  /**
   * The metasite's remaining FREE sub-agent runs — a different pool from the credit
   * balance above, and the number the plan card's "N free agent runs left" line states.
   * Null when the read was unavailable.
   *
   * Same source the native apps read: `wix.isa.balance.v1.BalanceService/GetBalance` →
   * `balance.subAgentBalance` (web via `BalanceClient.getFreeSubAgentsBalance` in
   * `/api/credits/balance`, native via `manage.wix.com/_api/balance/v1/balance`). The
   * route's own `balance` field folds the POC ledger in on top, so the indicator reads
   * `real` and never that sum.
   */
  freeAgentRuns: number | null;
}

interface BalanceResponse {
  balance?: number;
  /** The free sub-agent allowance alone, without the POC-ledger purchases. */
  real?: number;
  hasMetaSite?: boolean;
  account?: CreditAccount | null;
}

/** One settled read of /api/credits/balance — what the shared cache entry holds. */
export interface CreditsData {
  hasMetaSite: boolean;
  balance: number | null;
  account: CreditAccount | null;
  freeAgentRuns: number | null;
}

// `balance` is the BFF's spendable total, or null when there is no overview — never the route's own
// `json.balance`, which is the free sub-agent + POC-ledger pool the payments picker spends and is a
// different thing entirely (the sidebar chip once rendered it as a credit balance).
export function parseBalanceResponse(json: BalanceResponse): CreditsData {
  const account = json.account ?? null;
  const balance = account?.combinedCredits ?? null;
  return {
    hasMetaSite: json.hasMetaSite !== false,
    balance,
    account,
    freeAgentRuns: typeof json.real === 'number' ? json.real : null,
  };
}

async function fetchCreditsBalance(): Promise<CreditsData> {
  const res = await apiFetch(`${API_BASE}/api/credits/balance`, { credentials: 'include' });
  if (!res.ok) throw new Error(String(res.status));
  return parseBalanceResponse((await res.json()) as BalanceResponse);
}

// On a CREDITS_REFRESH_EVENT (fired after a purchase), poll the balance until it grows past its
// pre-action value or the budget is spent — so the sidebar/settings reflect the grant on their own.
// Module-level: one burst per event however many surfaces are mounted, and the shared cache entry
// it writes is what every mounted hook renders, so one burst updates them all.
let burstInFlight = false;
function onCreditsRefresh() {
  if (burstInFlight) return; // a burst is already in flight
  burstInFlight = true;
  // Pre-action balance = the early-stop baseline. If the event fired before the initial load
  // resolved (baseline still null), adopt the first observed value below so a LATER grant is
  // still caught as growth — otherwise `grew` stays false and the burst runs the full budget.
  let baseline = queryClient.getQueryData<CreditsData>(queryKeys.creditsBalance)?.balance ?? null;
  let attempts = 0;
  const tick = async () => {
    let bal: number | null = null;
    try {
      // staleTime 0 forces a network read: the burst exists to observe a change, so a cache hit
      // inside the client's default staleness window would defeat it.
      bal = (await queryClient.fetchQuery({ queryKey: queryKeys.creditsBalance, queryFn: fetchCreditsBalance, staleTime: 0 }))
        .balance;
    } catch {
      // A failed poll spends an attempt but doesn't end the burst — the grant may still land.
    }
    if (baseline == null) baseline = bal;
    const grew = baseline != null && bal != null && bal > baseline;
    if (grew || attempts >= REFRESH_DELAYS_MS.length) {
      burstInFlight = false;
      return;
    }
    setTimeout(tick, REFRESH_DELAYS_MS[attempts]);
    attempts += 1;
  };
  void tick();
}

// The window listener is installed while at least one consumer is mounted. Refcounted because
// several surfaces call useCredits at once, and removeEventListener on one unmount must not
// unsubscribe the others.
let refreshListeners = 0;

/**
 * The credit balance, from ONE shared React Query cache entry — the sidebar chip, the settings
 * cards and any future surface render the same settled read, so two of them can never show two
 * different balances at the same moment. A fresh mount past the client's staleTime (opening
 * Settings, say) refetches, and the refetch updates every mounted surface together — which is the
 * bug this replaced: each hook instance held its own useState snapshot, so an always-mounted
 * sidebar kept the number from page load while the settings card fetched a newer one beside it.
 */
/**
 * Whether the credits read left us with NOTHING to show — which is NOT the same as "a request failed".
 *
 * Every surface drops its whole card on this flag (`use-credits-summary` returns null, the mobile plan
 * card hides its credits block), so the distinction is the difference between a card that is absent and
 * a card that lies.
 *
 * React Query reports a failed BACKGROUND refetch as `status: 'error'` while KEEPING the last good
 * payload — `QueryObserverRefetchErrorResult` carries `data` and `isError: true` together. So reading
 * `isError` alone blanks a card whose correct numbers are sitting in cache, and with `retry: false`
 * plus `refetchOnWindowFocus: false` nothing brings it back until the next mount.
 *
 * That was survivable while the balance was read about once per page load. It stopped being survivable
 * when this hook started refetching on every credit movement: one transient 500 in the middle of an
 * agent run would take the sidebar chip away for the rest of the session — on the very surface the
 * live updates exist to improve.
 *
 * Pure and exported so the rule is testable without rendering, per orion-web/CLAUDE.md.
 */
export function creditsReadFailed(query: { isError: boolean; data?: unknown }): boolean {
  return query.isError && query.data === undefined;
}

export function useCredits(): CreditsState {
  const query = useQuery({ queryKey: queryKeys.creditsBalance, queryFn: fetchCreditsBalance });

  /*
   * Push: refetch when the account's credits actually move, so a bar follows a running agent rather
   * than the page load. Everything below stays — the channel is best-effort (the publish is swallowed
   * server-side, and off-turn movements carry no metasite to publish to), so the purchase burst and the
   * daily-reset timer remain the guarantees and this is the fast path.
   *
   * ⚠️ **WEB-ONLY, and a gap rather than a capability exception** (three-surface GOLDEN RULE, root
   * CLAUDE.md). Both phones draw these same bars and already subscribe to other `individuals-*`
   * channels, so nothing stops them doing this; it was scoped to web by request. Written down here and
   * in orion-web/CLAUDE.md because the rule requires the exception at the call site, and because the
   * feature must not be reported as shipped on the web half alone.
   */
  useCreditsDuplexer();

  useEffect(() => {
    if (refreshListeners === 0) window.addEventListener(CREDITS_REFRESH_EVENT, onCreditsRefresh);
    refreshListeners += 1;
    return () => {
      refreshListeners -= 1;
      if (refreshListeners === 0) window.removeEventListener(CREDITS_REFRESH_EVENT, onCreditsRefresh);
    };
  }, []);

  /*
   * Refetch once when today's allowance ROLLS OVER.
   *
   * The client is otherwise blind to it: the shared client sets `staleTime` 30s and
   * `refetchOnWindowFocus: false`, and the only thing that forces a read is a completed purchase. So a
   * page left open across the reset kept rendering the spent day indefinitely — "Daily 100 of 100"
   * after the allowance had already refilled, i.e. telling a user they are blocked when they are not.
   *
   * The PERIOD meter has the same staleness and does not need this: a month is not a sitting. A day
   * is — the boundary is UTC midnight, which is 4-5pm for US-West and 2-3am for Israel, so it lands
   * inside a working session for real users rather than at a quiet hour.
   *
   * **Only ever scheduled into the future**, which is what keeps it from looping: an already-past
   * `resetsAt` schedules nothing, so an invalidate can never trigger a refetch that returns the same
   * past timestamp and invalidates again. The skew lets the server's own roll land first — the ceiling
   * is recomputed per request from `now`, so asking a beat early would just re-read the spent day and
   * arm no second attempt.
   */
  const resetsAtIso = query.data?.account?.dailyLimit?.resetsAtIso ?? null;
  useEffect(() => {
    if (!resetsAtIso) return;
    const delay = dailyResetDelayMs(resetsAtIso, Date.now());
    if (delay === null) return;
    scheduleDailyReset(resetsAtIso, delay);
  }, [resetsAtIso]);

  const data = query.data;
  return {
    loading: query.isLoading,
    failed: creditsReadFailed(query),
    hasMetaSite: data?.hasMetaSite ?? true,
    balance: data?.balance ?? null,
    account: data?.account ?? null,
    freeAgentRuns: data?.freeAgentRuns ?? null,
  };
}
